import React, { useState, useRef, useEffect } from 'react';
import { Box, Flex, ActionIcon, Text, Avatar, Button, TextInput, Stack, Modal, Drawer, ScrollArea, Badge, Popover } from '@mantine/core';
import { IconHeart, IconMessageCircle, IconShoppingCart, IconShare, IconRobot, IconSend, IconFilter } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';



const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.ailinkb.shop';

export default function MarketplaceFeed({ addToCart }) {
  const { t } = useTranslation();
  const [chatOpen, setChatOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [tagsOpen, setTagsOpen] = React.useState(false);
  const [selectedVideo, setSelectedVideo] = React.useState(null);
  const [selectedVideoIndex, setSelectedVideoIndex] = React.useState(null);
  const [activeExpandedIndex, setActiveExpandedIndex] = React.useState(0);
  const [rotatorIndex, setRotatorIndex] = React.useState(0);
  const scrollContainerRef = React.useRef(null);
  
  React.useEffect(() => {
    if (selectedVideoIndex !== null && scrollContainerRef.current) {
      setActiveExpandedIndex(selectedVideoIndex);
      // scroll to the selected video immediately
      scrollContainerRef.current.scrollTop = selectedVideoIndex * window.innerHeight;
    }
  }, [selectedVideoIndex]);

  // Zduplikujmy wideo by wypełnić całe tło (np. 16 kafelków)
  
  const [dbVideos, setDbVideos] = React.useState([]);

  React.useEffect(() => {
    fetch(`${API_BASE_URL}/api/skills/approved`)
      .then(res => res.json())
      .then(data => {
        const mapped = data.map(skill => ({
          id: skill.id,
          url: skill.videoUrl?.startsWith('/') ? `${API_BASE_URL}${skill.videoUrl}` : skill.videoUrl,
          author: skill.ownerEmail.split('@')[0],
          ownerEmail: skill.ownerEmail,
          title: skill.title,
          description: skill.title + ' - ' + skill.description,
          likes: Math.floor(Math.random() * 500) + 10,
          comments: Math.floor(Math.random() * 50) + 1,
          price: skill.price,
          tags: skill.tags ? skill.tags.split(/[,#]+/).map(t => t.trim()).filter(Boolean).map(t => '#' + t) : []
        }));
        setDbVideos(mapped);
      })
      .catch(err => console.error('Error fetching skills:', err));
  }, []);

  const bgVideos = dbVideos;
  const isMarquee = bgVideos.length >= 4;

  const handleVideoEnded = React.useCallback(() => {
    setRotatorIndex(prev => (prev + 1) % bgVideos.length);
  }, [bgVideos.length]);

  React.useEffect(() => {
    if (!isMarquee && bgVideos.length > 1) {
      // Fallback interval just in case (e.g. for youtube iframes that don't trigger onEnded)
      const interval = setInterval(() => {
        setRotatorIndex(prev => (prev + 1) % bgVideos.length);
      }, 15000);
      return () => clearInterval(interval);
    }
  }, [isMarquee, bgVideos.length]);

    const allTags = React.useMemo(() => {
    const tags = new Set();
    dbVideos.forEach(v => {
      if (v.tags) v.tags.forEach(t => tags.add(t));
    });
    return Array.from(tags);
  }, [dbVideos]);

  return (
    <Box style={{ position: 'relative', width: '100%', height: '100%', backgroundColor: '#000', overflow: 'hidden' }}>
      
      {/* Tło Wideo - Jeden rząd na pełną wysokość */}
      <Box style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1, overflow: 'hidden' }}>
        {isMarquee ? (
          <div className="marquee-container">
            {/* Render 2 sets for infinite loop */}
            <div style={{ display: 'flex', height: '100%', gap: '20px', paddingRight: '20px' }}>
              {bgVideos.map((video, idx) => (
                <Box key={idx} style={{ height: '100%', aspectRatio: '9/16', cursor: 'pointer', flexShrink: 0 }} onClick={() => setSelectedVideoIndex(idx)}>
                  <VideoItem video={video} addToCart={addToCart} />
                </Box>
              ))}
            </div>
            <div style={{ display: 'flex', height: '100%', gap: '20px', paddingRight: '20px' }}>
              {bgVideos.map((video, idx) => (
                <Box key={'copy-'+idx} style={{ height: '100%', aspectRatio: '9/16', cursor: 'pointer', flexShrink: 0 }} onClick={() => setSelectedVideoIndex(idx)}>
                  <VideoItem video={video} addToCart={addToCart} />
                </Box>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {bgVideos.map((video, idx) => (
              <Box 
                key={idx} 
                style={{ 
                  position: 'absolute', 
                  height: '100%', 
                  aspectRatio: '9/16', 
                  cursor: 'pointer', 
                  opacity: idx === rotatorIndex ? 1 : 0, 
                  transition: 'opacity 1s ease-in-out',
                  zIndex: idx === rotatorIndex ? 2 : 1
                }} 
                onClick={() => setSelectedVideoIndex(idx)}
              >
                <VideoItem 
                  video={video} 
                  addToCart={addToCart} 
                  onEnded={idx === rotatorIndex ? handleVideoEnded : undefined} 
                  isActive={idx === rotatorIndex}
                />
              </Box>
            ))}
          </div>
        )}
      </Box>

      {/* Nakładka Cieniująca (żeby interfejs był czytelny) */}
      <Box style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'linear-gradient(to bottom, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.8) 100%)', zIndex: 2, pointerEvents: 'none' }} />

      {/* Pływający Interfejs na Wierzchu */}
      <Box style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 3, pointerEvents: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Stack align="center" style={{ pointerEvents: 'auto', width: '100%', maxWidth: 600, padding: '20px' }}>
          
          <Text c="white" fw={900} size="3rem" ta="center" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.8)' }}>
            {t('hero_title_discover')} <br/>
            <Text span variant="gradient" gradient={{ from: '#ff4b1f', to: '#ff9068', deg: 45 }}>{t('hero_title_skills')}</Text>
          </Text>

          <Flex 
            align="center" 
            style={{ width: '100%', backgroundColor: 'rgba(20, 20, 20, 0.7)', backdropFilter: 'blur(15px)', borderRadius: 30, padding: '10px 15px 10px 25px', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}
          >
            <IconRobot color="#fff" size={28} style={{ marginRight: 15 }} />
            <TextInput 
              placeholder={t('marketplace_search_placeholder')}
              variant="unstyled"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.currentTarget.value)}
              style={{ flex: 1 }}
              styles={{ input: { color: '#fff', fontSize: '1.2rem' } }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setChatOpen(true);
                }
              }}
            />
            <ActionIcon radius="xl" size="xl" variant="subtle" color="gray" onClick={() => setChatOpen(true)} style={{ marginRight: 10 }}>
              <IconFilter size={24} color="#fff" />
            </ActionIcon>
            <ActionIcon radius="xl" size="xl" color="indigo" variant="filled" onClick={() => setChatOpen(true)}>
              <IconSend size={24} />
            </ActionIcon>
          </Flex>

        </Stack>
      </Box>

      {/* Modal / Pełny ekran do oglądania feedu (TikTok style) */}
      <Modal
        opened={selectedVideoIndex !== null}
        onClose={() => setSelectedVideoIndex(null)}
        fullScreen
        withCloseButton={false}
        styles={{ content: { backgroundColor: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(10px)' }, inner: { padding: 0 } }}
      >
        {selectedVideoIndex !== null && (
          <Box 
            ref={scrollContainerRef}
            style={{ width: '100vw', height: '100vh', overflowY: 'scroll', scrollSnapType: 'y mandatory' }}
            onScroll={(e) => {
              const st = e.target.scrollTop;
              const vh = window.innerHeight;
              const activeIdx = Math.round(st / vh);
              if (activeIdx !== activeExpandedIndex) {
                setActiveExpandedIndex(activeIdx);
              }
            }}
          >
            <ActionIcon 
              color="white" 
              size="xl" 
              radius="xl" 
              variant="transparent" 
              style={{ position: 'fixed', top: 20, right: 20, zIndex: 100 }}
              onClick={() => setSelectedVideoIndex(null)}
            >
              <Text size="2rem">✖</Text>
            </ActionIcon>

            {bgVideos.map((video, idx) => (
              <Box 
                key={'feed-'+idx}
                style={{ width: '100%', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', scrollSnapAlign: 'start', position: 'relative' }}
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setSelectedVideoIndex(null);
                  }
                }}
              >
                <Box style={{ height: '90vh', aspectRatio: '9/16', position: 'relative', borderRadius: 20, overflow: 'hidden', boxShadow: '0 0 50px rgba(99,102,241,0.5)' }}>
                  <VideoItem video={video} isExpanded={idx === activeExpandedIndex} addToCart={addToCart} />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Modal>

      {/* Drawer z Czatem AI */}
      <Drawer opened={chatOpen} onClose={() => setChatOpen(false)} position="right" size="md" title={<Flex align="center"><IconFilter size={24} color="#6366f1" style={{ marginRight: 10 }}/><Text fw={700}>{t("filter_tags_title", "Filtruj Tagami")}</Text></Flex>} styles={{ content: { backgroundColor: '#1a1b1e', color: '#fff' }, header: { backgroundColor: '#1a1b1e', color: '#fff' }, close: { color: '#fff' } }}>
        <Box p="md">
          <Text size="sm" c="dimmed" mb="xl">{t("filter_tags_desc", "Wybierz technologie i modele AI, których szukasz w bazie:")}</Text>
          <Flex wrap="wrap" gap="sm">
            {allTags.map(tag => {
              const isActive = searchQuery.includes(tag);
              return (
                <Badge 
                  key={tag} 
                  variant={isActive ? "filled" : "outline"} 
                  color={isActive ? "indigo" : "gray"} 
                  size="xl" 
                  radius="md"
                  style={{ 
                    cursor: 'pointer', 
                    backgroundColor: isActive ? '#4f46e5' : 'transparent', 
                    color: '#fff', 
                    borderColor: isActive ? 'transparent' : '#555',
                    textTransform: 'none',
                    fontWeight: 500,
                    padding: '10px 15px',
                    height: 'auto'
                  }}
                  onClick={() => {
                    if (isActive) {
                      setSearchQuery(searchQuery.replace(tag, '').trim());
                    } else {
                      setSearchQuery((searchQuery + ' ' + tag).trim());
                    }
                  }}
                >
                  {tag}
                </Badge>
              )
            })}
          </Flex>
          <Button 
            fullWidth 
            mt="xl" 
            size="lg" 
            color="indigo" 
            variant="filled" 
            onClick={() => setChatOpen(false)}
          >
            {t("apply_filters_btn", "Zastosuj Filtry")}
          </Button>
        </Box>
      </Drawer>

    </Box>
  );
}


// Pojedynczy "TikTok" - widok jednego filmiku (Karta w siatce)
function VideoItem({ video, isExpanded = false, addToCart, onEnded, isActive = true }) {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = React.useState(false);
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    if (videoRef.current) {
      if (isActive) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(e => console.log(e));
      } else {
        videoRef.current.pause();
      }
    }
  }, [isActive]);

  return (
    <Box 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ 
        height: '100%', 
        width: '100%', 
        position: 'relative', 
        borderRadius: 16, 
        overflow: 'hidden',
        cursor: 'pointer',
        boxShadow: isHovered ? '0 10px 30px rgba(99, 102, 241, 0.4)' : 'none',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-5px)' : 'none'
      }}
    >
      {video.url && (video.url.includes('youtube.com') || video.url.includes('youtu.be')) ? (
        <iframe
          src={video.url.replace('watch?v=', 'embed/').replace('shorts/', 'embed/') + '?autoplay=' + ((isHovered || isExpanded) ? '1' : '0') + (isExpanded ? '&mute=0&controls=1' : '&mute=1&controls=0') + '&loop=1'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          style={{ width: '100%', height: '100%', border: 'none', pointerEvents: isExpanded ? 'auto' : 'none' }}
        />
      ) : (
        <video 
          ref={videoRef}
          src={video.url} 
          autoPlay={isActive}
          playsInline={true}
          preload="auto"
          loop={onEnded ? false : true} 
          muted={!isExpanded}
          controls={isExpanded}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onEnded={onEnded}
        />
      )}
      
      {/* Nakładka przyciemniająca na dole dla lepszej czytelności tekstu */}
      <Box style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%', background: 'linear-gradient(transparent, rgba(0,0,0,0.9))', pointerEvents: 'none' }} />

      {/* Informacje o filmiku (lewy dół) */}
      <Box style={{ position: 'absolute', bottom: 20, left: 15, right: 60, color: '#fff', pointerEvents: 'none' }}>
        <Text size="md" fw={700}>@{video.author}</Text>
        <Text size="xs" mt={5} lineClamp={3}>{video.description}</Text>
        <Flex gap="xs" mt={10} wrap="wrap">
          {video.tags.map(tag => (
            <Text key={tag} size="10px" fw={700} style={{ color: '#a5b4fc', backgroundColor: 'rgba(79, 70, 229, 0.2)', padding: '2px 6px', borderRadius: 10 }}>{tag}</Text>
          ))}
        </Flex>
      </Box>

      {/* Przycisk Zakupu */}
      <Box style={{ position: 'absolute', bottom: 20, right: 15 }}>
        <Flex direction="column" align="center">
          <ActionIcon 
            radius="xl" 
            size="lg" 
            color="indigo" 
            variant="filled" 
            style={{ width: 40, height: 40, boxShadow: '0 0 10px rgba(99, 102, 241, 0.5)' }}
            onClick={(e) => {
            e.stopPropagation();
            if (addToCart) {
              addToCart({
                skillId: video.id,
                serviceName: video.description ? video.description.substring(0, 30) + '...' : 'AI Skill',
                ownerEmail: video.author,
                amount: video.price,
                currency: 'EUR'
              });
            }
          }}
          >
            <IconShoppingCart size={20} />
          </ActionIcon>
          <Text size="10px" c="white" fw={800} mt={5}>{video.price} EUR</Text>
        </Flex>
      </Box>
    </Box>
  );
}